import { usePostTransactions } from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { ModalTextInputDropdown } from '@fragment/ui/src/components/Modal/ModalTextInputDropdown';
import { Toast } from '@fragment/ui/src/components/Toast';

type FormData = {
  external_id: string;
  posted: string;
  currency: string;
  amount: string;
};

type SyncTransactionModalProps = {
  accountId: string;
  onClose: VoidFunction;
};

export const SyncTransactionModal = ({
  accountId,
  onClose,
}: SyncTransactionModalProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const postTransactionMutation = usePostTransactions();

  const currencyOptions = useMemo(() => ['USD', 'EUR', 'GBP'], []);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      external_id: '',
      posted: new Date().toISOString().split('T')[0],
      currency: 'USD',
      amount: '',
    },
  });

  const currency = watch('currency');

  const onSubmit = useCallback(
    async (formData: FormData) => {
      try {
        await postTransactionMutation.mutateAsync({
          data: {
            external_id: formData.external_id,
            account: { id: accountId },
            posted: new Date(formData.posted).toISOString(),
            currency: formData.currency as 'USD' | 'EUR' | 'GBP',
            amount: formData.amount,
            allocations: [],
          },
        });

        await queryClient.invalidateQueries({
          queryKey: ['/transactions'],
        });

        showToast(
          <Toast type="success" message="Transaction synced successfully" />
        );

        onClose();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to sync transaction. Please try again.';
        showToast(
          <Toast
            type="error"
            message={`Failed to sync transaction: ${errorMessage}`}
          />
        );
      }
    },
    [postTransactionMutation, accountId, queryClient, showToast, onClose]
  );

  const canSubmit = isValid && !postTransactionMutation.isPending;

  return (
    <Modal
      data-testid="sync-transaction-modal"
      onClose={onClose}
      width="sm"
      onSubmit={handleSubmit(onSubmit)}
      header={<div className="text-main">Sync Transaction</div>}
      body={
        <div className="flex flex-col space-y-f2">
          <div className="flex flex-col space-y-f2">
            <ModalField
              hasError={!!errors.external_id}
              label="External ID"
              labelWidth="w-f12"
            >
              <ModalTextInput
                data-testid="external-id-input"
                placeholder="External ID"
                {...register('external_id', {
                  required: 'External ID is required',
                })}
              />
            </ModalField>
            <ModalField
              hasError={!!errors.posted}
              label="Posted"
              labelWidth="w-f12"
            >
              <ModalTextInput
                data-testid="posted-input"
                type="date"
                {...register('posted', {
                  required: 'Posted date is required',
                })}
              />
            </ModalField>
            <ModalField label="Currency" labelWidth="w-f12">
              <ModalTextInputDropdown
                data-testid="currency-select"
                options={currencyOptions}
                value={currency}
                onChange={(value) => setValue('currency', value)}
                placeholder="Select currency"
              />
            </ModalField>
            <ModalField
              hasError={!!errors.amount}
              label="Amount"
              labelWidth="w-f12"
            >
              <ModalTextInput
                data-testid="amount-input"
                placeholder="e.g., -1000 or 1000"
                {...register('amount', {
                  required: 'Amount is required',
                })}
              />
            </ModalField>
          </div>
        </div>
      }
      footer={
        <div className="flex flex-row justify-between w-full">
          <FormButtons
            type="secondary"
            cancelButtonLabel="Cancel"
            submitButtonLabel={
              postTransactionMutation.isPending ? 'Syncing...' : 'Sync'
            }
            onCancel={onClose}
            onSubmit={handleSubmit(onSubmit)}
            disabled={!canSubmit}
          />
        </div>
      }
    />
  );
};
